import { Anchor, Badge, Breadcrumbs, Button, Card, Grid, Group, Select, SimpleGrid, Stack, Tabs, Text, Title } from "@mantine/core";
import {
  IconCamera, IconCoin, IconCube, IconFiles, IconLayoutDashboard, IconMap2, IconSettings, IconTableImport,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Chart } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { ReportButton } from "../../components/ReportButton";
import { Empty, fmtNum, SEASONS } from "../../components/ui";
import { UnitHero } from "../../components/UnitHero";
import { useT } from "../../i18n/script";
import { DocumentsPanel } from "../dms/DmsPage";
import { ImportPanel } from "../imports/ImportPage";
import MapPage from "../MapPage";
import { type Producer, ProducerForm } from "../producers/ProducersPage";
import { speciesColor, useSpeciesNames } from "./HoldingsPage";

interface OrchardItem {
  id: number; name: string; species: string; cultivar: string | null; planting_year: number | null; organic: boolean; hail_net: boolean;
  area_ha: number; trees: number; harvest_kg: number; diary_entries: number; has_3d: boolean; photos: number; status: string;
}
interface Detail extends Producer {
  season: number; harvest_kg: number; orchards: OrchardItem[]; sales: Record<string, unknown>[]; bbox_3857: number[] | null;
}

/** Karton gazdinstva: pregled (alfanumerički), mapa (grafički → voćnjak → 3D), dokumenti i upravljanje (izmjena, uvoz). */
export default function HoldingPage() {
  const { id } = useParams();
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const sp = useSpeciesNames();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "pregled";
  const setTab = (v: string | null) => v && setParams({ tab: v }, { replace: true });
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [focus3d, setFocus3d] = useState<number | null>(null);  // voćnjak čiji se 3D otvara na kartici „Mapa i 3D“
  const d = useQuery({ queryKey: ["producer", id, season], queryFn: () => http.get<Detail>(`/api/v1/producers/${id}`, { season }) });
  const geo = useQuery({ queryKey: ["holding-geo", id], queryFn: () => http.get<{ type: string; features: unknown[] }>("/api/v1/orchards/geojson", { producer_id: id }) });
  const hist = useQuery({ queryKey: ["producer-hist", d.data?.bpg], enabled: !!d.data && can("analytics:read"),
    queryFn: () => http.get<{ seasons: Record<string, unknown>[] }>(`/api/v1/analytics/producers/${d.data!.bpg}`) });
  if (d.error) return <Text c="red">{(d.error as Error).message}</Text>;
  if (!d.data) return <Text>{t("Učitavanje...")}</Text>;
  const p = d.data;
  const sold = p.sales.reduce((s, x) => s + Number(x.value_km ?? 0), 0);
  const manage = can("producers:write") || can("data:import");

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor size="sm" onClick={() => nav("/gazdinstva")}>{t("Gazdinstva i voćnjaci")}</Anchor>
        <Text size="sm">{p.name}</Text>
      </Breadcrumbs>
      <UnitHero geojson={geo.data} onMapClick={() => setTab("mapa")}
        subtitle={<>BPG {p.bpg} · {p.holder_name ?? "—"} · {t(p.opstina ?? "")}</>}
        title={p.name}
        badges={<>
          <Badge color={p.rpg_status === "aktivan" ? "green" : "gray"} variant="filled">{t(`RPG: ${p.rpg_status}`)}</Badge>
          {p.species.map((s) => <Badge key={s} variant="white" style={{ color: speciesColor(s) }}>{t(sp(s))}</Badge>)}
        </>}
        stats={[{ label: t("voćnjaka"), value: fmtNum(p.orchard_count, 0) }, { label: "ha", value: fmtNum(p.area_ha, 2) },
          { label: t("stabala"), value: fmtNum(p.tree_count, 0) }, { label: t(`ubrano ${season}, t`), value: fmtNum(p.harvest_kg / 1000, 1) }]}
        actions={<>
          <Button size="xs" color="lime" leftSection={<IconMap2 size={14} />} onClick={() => setTab("mapa")}>{t("Mapa")}</Button>
          <Select size="xs" w={92} data={SEASONS} value={season} onChange={(v) => v && setSeason(v)} aria-label={t("Sezona")} />
          {can("subsidies:read") && (
            <Button size="xs" variant="white" leftSection={<IconCoin size={14} />}
              onClick={() => nav(`/podsticaji?tab=provjera&gazdinstvo=${p.id}&godina=${season}`)}>{t("Podsticaji")}</Button>
          )}
          <ReportButton code="godisnji_proizvodjac" params={{ bpg: p.bpg, sezona: Number(season) }} label="Godišnji izvještaj" size="xs" />
        </>} />

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="pregled" leftSection={<IconLayoutDashboard size={15} />}>{t("Pregled")}</Tabs.Tab>
          <Tabs.Tab value="mapa" leftSection={<IconMap2 size={15} />}>{t("Mapa i 3D")}</Tabs.Tab>
          {can("dms:read") && <Tabs.Tab value="dokumenti" leftSection={<IconFiles size={15} />}>{t("Upravljanje dokumentima")}</Tabs.Tab>}
          {manage && <Tabs.Tab value="upravljanje" leftSection={<IconSettings size={15} />}>{t("Upravljanje i uvoz")}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="pregled" pt="md">
          <Stack>
            <Title order={4}>{t("Voćnjaci")}</Title>
            {!p.orchards.length ? <Empty text="Gazdinstvo nema voćnjaka — dodajte ih u kartici Upravljanje i uvoz (novi voćnjak ili uvoz iz Excela)" /> : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {p.orchards.map((o) => (
                  <Card key={o.id} withBorder radius="lg" className="holding-card" onClick={() => nav(`/vocnjaci/${o.id}`)}
                    style={{ borderLeft: `5px solid ${speciesColor(o.species)}` }} tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && nav(`/vocnjaci/${o.id}`)}>
                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                      <div style={{ minWidth: 0 }}>
                        <Text fw={750} lineClamp={1}>{o.name}</Text>
                        <Text size="xs" c="dimmed">{t(sp(o.species))}{o.cultivar ? `, ${o.cultivar}` : ""}{o.planting_year ? ` — ${o.planting_year}.` : ""}</Text>
                      </div>
                      <Group gap={4}>
                        {o.has_3d && (
                          <Badge size="xs" color="green" leftSection={<IconCube size={10} />} style={{ cursor: "pointer" }}
                            title={t("Otvori 3D")} onClick={(e) => { e.stopPropagation(); setFocus3d(o.id); setTab("mapa"); }}>3D</Badge>
                        )}
                        {o.photos > 0 && <Badge size="xs" color="gray" variant="light" leftSection={<IconCamera size={10} />}>{o.photos}</Badge>}
                      </Group>
                    </Group>
                    <Group gap="lg" mt="sm">
                      <div><Text fw={750}>{fmtNum(o.area_ha, 2)}</Text><Text size="xs" c="dimmed">ha</Text></div>
                      <div><Text fw={750}>{fmtNum(o.trees, 0)}</Text><Text size="xs" c="dimmed">{t("stabala")}</Text></div>
                      <div><Text fw={750}>{fmtNum(o.harvest_kg / 1000, 1)}</Text><Text size="xs" c="dimmed">{t(`t u ${season}`)}</Text></div>
                      <div><Text fw={750}>{fmtNum(o.diary_entries, 0)}</Text><Text size="xs" c="dimmed">{t("upisa u knjigu")}</Text></div>
                    </Group>
                  </Card>
                ))}
              </SimpleGrid>
            )}
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder h="100%">
                  <Title order={5} mb="xs">{t(`Prodaja ${season}`)}</Title>
                  <DataTable rows={p.sales} search={false} dense columns={[
                    { key: "partner_name", label: "Kupac" }, { key: "partner_entity", label: "Entitet" },
                    { key: "kg", label: "Količina (kg)", type: "number", decimals: 0 }, { key: "value_km", label: "Vrijednost (KM)", type: "money" },
                  ]} totals={{ kg: p.sales.reduce((s, x) => s + Number(x.kg), 0), value_km: sold }} />
                </Card>
              </Grid.Col>
              {can("analytics:read") && (
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card withBorder h="100%">
                    <Title order={5} mb="xs">{t("Kroz sezone: ubrano (t) i prinos (t/ha)")}</Title>
                    <Chart rows={hist.data?.seasons ?? []} x="sezona" type="line" height={220}
                      series={[{ key: "ubrano_t", label: t("Ubrano (t)") }, { key: "prinos_t_ha", label: t("Prinos (t/ha)") }]} />
                  </Card>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="mapa" pt="md">
          {p.orchards.some((o) => o.has_3d) && (
            <Group gap="xs" mb="xs">
              {p.orchards.filter((o) => o.has_3d).map((o) => (
                <Button key={o.id} size="xs" leftSection={<IconCube size={15} />} variant={focus3d === o.id ? "filled" : "light"}
                  onClick={() => { setFocus3d(null); setTimeout(() => setFocus3d(o.id), 0); }}>{`3D — ${o.name}`}</Button>
              ))}
            </Group>
          )}
          <MapPage key={focus3d ?? "mapa"} height="calc(100dvh - 260px)" compact fitExtent={p.bbox_3857} results={geo.data ?? null}
            orchardId={focus3d ?? undefined} open3d={focus3d != null} />
          <Text size="xs" c="dimmed" mt={6}>{t("3D: dugme iznad mape, ili klik na voćnjak (ili stablo) pa dugme 3D u kartici voćnjaka. U 3D prikazu dugme 2D vraća mapu.")}</Text>
        </Tabs.Panel>

        <Tabs.Panel value="dokumenti" pt="md"><DocumentsPanel holdingId={p.id} /></Tabs.Panel>

        <Tabs.Panel value="upravljanje" pt="md">
          <Grid>
            {can("producers:write") && (
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Card withBorder>
                  <Title order={5} mb="sm">{t("Podaci gazdinstva")}</Title>
                  <ProducerForm initial={p} onDone={() => d.refetch()} />
                </Card>
              </Grid.Col>
            )}
            {can("data:import") && (
              <Grid.Col span={{ base: 12, lg: can("producers:write") ? 7 : 12 }}>
                <Card withBorder>
                  <Group gap={6} mb="sm"><IconTableImport size={18} /><Title order={5}>{t("Uvoz podataka za ovo gazdinstvo")}</Title></Group>
                  <ImportPanel context={{ bpg: p.bpg }} holdingId={p.id} orchards={p.orchards.map((o) => ({ id: o.id, name: o.name }))}
                    only={["vocnjaci", "vocnjaci_gis", "tretmani", "dubrenje", "berba", "fenologija", "fotografije_zip"]} />
                </Card>
              </Grid.Col>
            )}
          </Grid>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
